/* eslint-disable lingui/no-unlocalized-strings */
import { t } from "@lingui/macro";
import {
  ArrowsClockwise,
  ArrowSquareOut,
  ArrowUp,
  CalendarBlank,
  CircleNotch,
  CreditCard,
  Crown,
  Warning,
} from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { isAxiosError } from "axios";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "@/client/hooks/use-toast";
import { axios } from "@/client/libs/axios";
import { messageFromServerError } from "@/client/services/errors/translate-error";
import { useUser } from "@/client/services/user";
import { useBillingStore } from "@/client/stores/billing";

const TIER_LABELS: Record<string, { name: string; color: string }> = {
  starter: { name: "Starter", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  pro: { name: "Pro", color: "bg-violet-500/10 text-violet-600 border-violet-500/20" },
  careerMax: { name: "Career Max", color: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  ultra: { name: "Ultra", color: "bg-rose-500/10 text-rose-600 border-rose-500/20" },
};

export const BillingSettings = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const openPaywall = useBillingStore((s) => s.openPaywall);
  const [portalLoading, setPortalLoading] = useState(false);

  if (!user) return null;

  const tier = user.planTier;
  const isMaxTier = tier === "ultra";
  const periodEnd = user.planCurrentPeriodEnd;
  const cancelling = user.planCancelAtPeriodEnd;
  const tierInfo = tier ? TIER_LABELS[tier] : null;

  const formattedEnd = periodEnd
    ? new Date(periodEnd).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const daysRemaining = periodEnd
    ? Math.max(0, Math.ceil((new Date(periodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await axios.post<{ url: string }>("/billing/portal");
      if (data.url) {
        window.location.href = data.url;
        return;
      }

      toast({
        variant: "error",
        title: t`We couldn't open billing. Please try again in a moment.`,
      });
    } catch (error) {
      if (!isAxiosError(error) || !messageFromServerError(error.response?.data)) {
        toast({
          variant: "error",
          title: t`We couldn't open billing. Please try again in a moment.`,
        });
      }
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold leading-relaxed tracking-tight">{t`Plan & Billing`}</h3>
        <p className="leading-relaxed opacity-75">
          {tier
            ? t`Manage your subscription, view billing details, and update your payment method.`
            : t`You don't have an active plan. Subscribe to unlock all features.`}
        </p>
      </div>

      {tier && tierInfo ? (
        <div className="space-y-4">
          {/* Cancellation banner */}
          {cancelling && (
            <div className="flex items-start gap-x-3 rounded-lg border border-warning/30 bg-warning/5 p-4">
              <Warning size={20} className="mt-0.5 shrink-0 text-warning" weight="fill" />
              <div>
                <p className="text-sm font-medium text-warning">Subscription cancelled</p>
                <p className="mt-0.5 text-sm opacity-70">
                  Your plan is still active until{" "}
                  <span className="font-medium">{formattedEnd}</span>. After that, you'll lose
                  access to {tierInfo.name} features. You can resubscribe anytime from the billing
                  portal.
                </p>
              </div>
            </div>
          )}

          {/* Plan card */}
          <div className="rounded-lg border bg-secondary/30 p-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-x-3">
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-lg border",
                    tierInfo.color,
                  )}
                >
                  <Crown size={20} weight="fill" />
                </div>
                <div>
                  <div className="flex items-center gap-x-2">
                    <span className="text-lg font-semibold">{tierInfo.name}</span>
                    <span
                      className={cn(
                        "rounded-full border px-2 py-0.5 text-xs font-medium",
                        cancelling
                          ? "border-warning/30 bg-warning/10 text-warning"
                          : tierInfo.color,
                      )}
                    >
                      {cancelling ? "Cancelling" : "Active"}
                    </span>
                  </div>
                  <p className="text-sm opacity-60">
                    {cancelling ? "Access ends at period end" : "Your current plan"}
                  </p>
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {formattedEnd && (
                <div className="flex items-center gap-x-2 text-sm">
                  <CalendarBlank size={16} className="shrink-0 opacity-50" />
                  <span className="opacity-60">{cancelling ? "Expires:" : "Renews:"}</span>
                  <span className="font-medium">{formattedEnd}</span>
                </div>
              )}
              {daysRemaining !== null && (
                <div className="flex items-center gap-x-2 text-sm">
                  <ArrowsClockwise size={16} className="shrink-0 opacity-50" />
                  <span className="opacity-60">
                    {cancelling ? "Access remaining:" : "Days remaining:"}
                  </span>
                  <span className={cn("font-medium", cancelling && "text-warning")}>
                    {daysRemaining} {daysRemaining === 1 ? "day" : "days"}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            {!isMaxTier && !cancelling && (
              <Button
                variant="primary"
                className="gap-x-2"
                onClick={() =>
                  openPaywall(undefined, "Upgrade to a higher plan for more features.")
                }
              >
                <ArrowUp size={16} weight="bold" />
                {t`Upgrade Plan`}
              </Button>
            )}
            <Button
              variant="outline"
              className="gap-x-2"
              disabled={portalLoading}
              onClick={handleManageBilling}
            >
              {portalLoading ? (
                <CircleNotch size={16} className="animate-spin" />
              ) : (
                <CreditCard size={16} />
              )}
              {cancelling ? t`Resubscribe` : t`Manage Billing`}
            </Button>
            {!cancelling && (
              <Button
                variant="ghost"
                className="gap-x-2 text-sm opacity-70"
                disabled={portalLoading}
                onClick={handleManageBilling}
              >
                <ArrowSquareOut size={14} />
                {t`Update Payment Method`}
              </Button>
            )}
          </div>

          <p className="text-xs opacity-50">
            {cancelling
              ? "You can resubscribe from the billing portal to keep your plan active."
              : "Billing is managed securely through Stripe. You can cancel, change plans, or update your payment method from the billing portal."}
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <Crown size={32} className="mx-auto mb-3 opacity-30" />
          <p className="mb-1 font-medium">{t`No active plan`}</p>
          <p className="mb-4 text-sm opacity-60">
            Subscribe to unlock AI features, templates, and more.
          </p>
          <Button variant="primary" className="gap-x-2" onClick={() => navigate("/#pricing")}>
            {t`View Plans`}
            <ArrowSquareOut size={14} />
          </Button>
        </div>
      )}
    </div>
  );
};
